import { useMemo } from 'react';
import { AdminController } from '../../../../controllers';
import constant from '../../../../utils/constant';
import {
	handleImageUpload,
	refreshQuery,
	removeEmptyKeys,
} from '../../../../utils/utils';
import { MimaToastUtil } from '../../../../components';
import { useFormik } from 'formik';

export const useAdminWebsiteConfigLogic = () => {
	const { data, isLoading } = AdminController.useGetBusinessDetails();

	const business = useMemo(() => {
		if (data?.status === constant.Success) {
			return data?.data;
		}
		return {};
	}, [data?.data, data?.status]);

	const { settings } = business || {};
	const { tawk, googleTag, pinterest, website } = settings || {};

	const {
		homeBanner,
		aboutUs,
		faqs,
		sizeGuides,
		shipping,
		collectionsMenuStyle,
		maintenanceMode,
		refundPolicy,
		termsOfService,
	} = website || {};

	const initialValues = {
		tawk: {
			propertyId: tawk?.propertyId || '',
			widgetId: tawk?.widgetId || '',
		},
		googleTag: {
			id: googleTag?.id || '',
			url: googleTag?.url || '',
		},
		pinterest: {
			id: pinterest?.id || '',
			email: pinterest?.email || '',
		},
		website: {
			homeBanner: {
				imageUrl: [],
				title: homeBanner?.title || '',
			},
			collectionsMenuStyle: collectionsMenuStyle || 'DISPLAY_ON_MENU_BAR',
			aboutUs: {
				imageUrl: [],
				text: aboutUs?.text || '',
			},

			faqs: faqs?.map(x => ({
				question: x.question,
				text: x.text,
			})) || [
				{
					question: '',
					text: '',
				},
			],
			sizeGuides: sizeGuides?.map(x => x) || [],
			shipping: {
				local: {
					shippingFee: shipping?.local?.shippingFee || 0,
					freeShippingForOrderAbove:
						shipping?.local?.freeShippingForOrderAbove || 0,
				},
				international: {
					shippingFee: shipping?.international?.shippingFee || 0,
					freeShippingForOrderAbove:
						shipping?.international?.freeShippingForOrderAbove || 0,
				},
			},
			maintenanceMode: maintenanceMode || false,
			refundPolicy: refundPolicy || '',
			refundPolicyDays: 5,
			useDefaultRefundPolicy: refundPolicy ? false : true,
			termsOfService: termsOfService || '',
			useDefaulTermsOfService: termsOfService ? false : true,
			privacyPolicy: '',
		},
		homeBannerImageUrl: homeBanner?.imageUrl
			? [
					{
						name: homeBanner?.imageUrl,
						preview: homeBanner?.imageUrl,
					},
				]
			: [],
		aboutUsImageUrl: aboutUs?.imageUrl
			? [
					{
						name: aboutUs?.imageUrl,
						preview: aboutUs?.imageUrl,
					},
				]
			: [],
	};
	const onSubmit = async values => {
		let homeBannerUrl = [];
		const file = values.homeBannerImageUrl?.[0];
		if (file) {
			homeBannerUrl = [file.preview];
			if (file.name !== file.preview) {
				homeBannerUrl = await handleImageUpload(values?.homeBannerImageUrl);
			}
		}
		let aboutUsUrl = [];
		const file2 = values.aboutUsImageUrl?.[0];
		if (file2) {
			aboutUsUrl = [file2.preview];
			if (file2.name !== file2.preview) {
				aboutUsUrl = await handleImageUpload(values?.aboutUsImageUrl);
			}
		}

		const payload = {
			tawk: values.tawk,
			googleTag: values.googleTag,
			pinterest: values.pinterest,

			website: {
				homeBanner: {
					imageUrl: homeBannerUrl.length > 0 ? homeBannerUrl[0] : '',
					title: values.website?.homeBanner?.title || '',
				},
				collectionsMenuStyle: values.website?.collectionsMenuStyle,
				aboutUs: {
					imageUrl: aboutUsUrl.length > 0 ? aboutUsUrl[0] : '',
					text: values.website?.aboutUs?.text || '',
				},
				faqs:
					values.website?.faqs?.map(faq => ({
						question: faq.question,
						text: faq.text,
					})) || [],
				sizeGuides: values.website?.sizeGuides || [],

				shipping: {
					local: values.website?.shipping?.local,
					international: values.website?.shipping?.international,
				},
				maintenanceMode: values.website?.maintenanceMode || false,
				refundPolicy: values.website?.refundPolicy || '',
				refundPolicyDays: values.website?.refundPolicyDays || 0,
				useDefaultRefundPolicy: values.website?.useDefaultRefundPolicy || false,
				termsOfService: values.website?.termsOfService || '',
				useDefaulTermsOfService:
					values.website?.useDefaulTermsOfService || false,
				privacyPolicy: values.website?.privacyPolicy || '',
				theme: website?.theme,
			},
		};

		removeEmptyKeys(payload);

		const { status, errorMessage } =
			await AdminController.updateSettings(payload);

		if (status === constant.Success) {
			refreshQuery({ queryKey: [constant.apiQueryKey.getBusinessDetails] });

			MimaToastUtil.success({
				message: constant.Success,
			});
			return;
		}

		MimaToastUtil.error({
			message: errorMessage,
		});
	};

	const formik = useFormik({
		initialValues,
		onSubmit,
	});

	const sections = useMemo(
		() => [
			{
				id: 'menuBar',
				title: 'Menu Bar Style',
				icon: 'menu',
			},
			// {
			// 	id: 'homeBanner',
			// 	title: 'Home Page Banner',
			// 	icon: 'image',
			// },
			// {
			// 	id: 'aboutUs',
			// 	title: 'About Us Page',
			// 	icon: 'info',
			// },
			{
				id: 'localShipping',
				title: 'Local Shipping',
				icon: 'truck',
			},
			{
				id: 'internationalShipping',
				title: 'International Shipping',
				icon: 'globe',
			},
			{
				id: 'refundPolicy',
				title: 'Refund Policy',
				icon: 'undo',
			},
			{
				id: 'termsOfService',
				title: 'Standard Terms of Services',
				icon: 'file-text',
			},
			{
				id: 'faqs',
				title: 'FAQs Page',
				icon: 'help-circle',
			},
			{
				id: 'sizeGuide',
				title: 'Size Guide',
				icon: 'ruler',
			},
			{
				id: 'maintenanceMode',
				title: 'Maintenance Mode',
				icon: 'wrench',
			},
			{
				id: 'liveChat',
				title: 'Live Chat',
				icon: 'message-circle',
			},
			{
				id: 'googleTag',
				title: 'Google Tag',
				icon: 'tag',
			},
			{
				id: 'pinterestTag',
				title: 'Pinterest Tag',
				icon: 'tag',
			},
		],
		[]
	);

	return { isLoading, formik, sections };
};

export default useAdminWebsiteConfigLogic;
